import React, { useMemo, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import ArchiveEntityDialog from "../../common/ArchiveEntityDialog";
import EditEntityDialog from "../common/EditEntityDialog";
import { formatEntityStatus, isArchived } from "../common/academicUtils";
import {
  useAcademicPrograms,
  useAllGrades,
  useArchiveAcademicProgram,
  useUpdateAcademicProgram
} from "../../../../hooks/api/education/useAcademicStructure";

const ProgramDetail: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { programId = "" } = useParams();
  const { data: programs = [], isPending, refetch } = useAcademicPrograms();
  const { data: grades = [] } = useAllGrades(programId);
  const updateProgram = useUpdateAcademicProgram();
  const archiveProgram = useArchiveAcademicProgram();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const program = useMemo(
    () => programs.find((item) => item.id === programId),
    [programs, programId]
  );

  const programGrades = useMemo(
    () => grades.filter((grade) => grade.programId === programId),
    [grades, programId]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-program", "Loading program...")}
      />
    );
  }

  if (!program) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("program-not-found", "Program not found.")}</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="program-detail-page" sx={{ p: 3 }}>
      <Link component={RouterLink} to="..">
        {t("back-to-programs", "Back to programs")}
      </Link>

      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 0.5 }}>
        {program.name}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {formatEntityStatus(program.status)}
        {program.code ? ` · ${program.code}` : ""}
      </Typography>

      {program.description ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {program.description}
        </Typography>
      ) : null}

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        {!isArchived(program.status) ? (
          <>
            <AppButton onClick={() => setEditOpen(true)}>
              {t("edit", "Edit")}
            </AppButton>
            <AppButton variant="text" onClick={() => setArchiveOpen(true)}>
              {t("archive", "Archive")}
            </AppButton>
          </>
        ) : null}
        <AppButton
          variant="secondary"
          onClick={() => navigate(`../grades?programId=${program.id}`)}
        >
          {t("view-grades", "View grades")}
        </AppButton>
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("grades-in-program", "Grades in this program")}
      </Typography>
      {programGrades.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t("grades-empty", "No grades yet for this program.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {programGrades.map((grade) => (
            <Box
              component="li"
              key={grade.id}
              sx={{
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Typography variant="body2">{grade.name}</Typography>
              <AppButton
                variant="text"
                size="small"
                onClick={() => navigate(`../grades/${grade.id}`)}
              >
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}

      <EditEntityDialog
        open={editOpen}
        title={t("edit-program", "Edit program")}
        loading={updateProgram.isPending}
        fields={[
          {
            key: "name",
            label: t("program-name-label", "Program name"),
            value: program.name
          },
          {
            key: "code",
            label: t("program-code-label", "Program code"),
            value: program.code ?? ""
          },
          {
            key: "description",
            label: t("program-description-label", "Description"),
            value: program.description ?? "",
            multiline: true
          }
        ]}
        onClose={() => setEditOpen(false)}
        onSave={async (values) => {
          await updateProgram.mutateAsync({
            programId: program.id,
            expectedVersion: program.version ?? undefined,
            updates: {
              name: values.name.trim(),
              code: values.code.trim() || undefined,
              description: values.description.trim() || undefined
            }
          });
          setEditOpen(false);
          refetch();
        }}
      />

      <ArchiveEntityDialog
        open={archiveOpen}
        entityLabel={program.name}
        loading={archiveProgram.isPending}
        onClose={() => setArchiveOpen(false)}
        onConfirm={async (reason) => {
          await archiveProgram.mutateAsync({
            programId: program.id,
            expectedVersion: program.version ?? undefined,
            reason: reason || undefined
          });
          setArchiveOpen(false);
          refetch();
        }}
      />
    </Box>
  );
};

export default ProgramDetail;
